/* /ess/leave — request leave for yourself + history (prototype pages3.js pgEssLeave). */
import { useEffect, useId, useState, type ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "../../data/provider";
import { useAiExtract, useClock, useCreateLeave, useLeaveBalance, useLeavePolicies, useLeaves, useUploadFile } from "../../data/hooks";
import { addDays, inclusiveDays } from "../../domain/dates";
import { useI18n } from "../../i18n";
import { hDate } from "../../lib/format";
import { Alert, Button, Card, Chip, DualDate, EmptyState, Field, Icon, Input, Select, errMsg, useToast } from "../../ui";
import { NoLink } from "./EssHome";
import { useEmployeeId, workingDays } from "./essLib";

function Body({ empId }: { empId: number }) {
  const { t, L } = useI18n();
  const toast = useToast();
  const nav = useNavigate();
  const clock = useClock();
  const today = clock.today;
  const bal = useLeaveBalance(empId);
  const pol = useLeavePolicies();
  const hist = useLeaves();
  const create = useCreateLeave();
  const extract = useAiExtract();
  const upload = useUploadFile();
  const ids = { type: useId(), from: useId(), to: useId(), note: useId(), msg: useId(), file: useId() };
  const [type, setType] = useState("Annual");
  const [from, setFrom] = useState(() => addDays(today, 30));
  const [to, setTo] = useState(() => addDays(today, 34));
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState("");
  const [attach, setAttach] = useState<{ key: string; name: string } | null>(null);
  const [fe, setFe] = useState<Record<string, string>>({});
  /* Defaults follow the server clock until the user edits a date (the clock may load after first render). */
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (clock.ready && !touched) { setFrom(addDays(clock.today, 30)); setTo(addDays(clock.today, 34)); }
  }, [clock.ready, clock.today, touched]);

  const remaining = bal.data?.remaining ?? 0;
  const cal = inclusiveDays(from, to);
  const work = workingDays(from, to);
  const badRange = !from || !to || to < from;
  const over = type === "Annual" && !!bal.data && cal > remaining;
  const past = !!from && from < today;
  const policy = pol.data?.find((p) => p.type === type);
  const mine = (hist.data || []).filter((l) => l.employeeId === empId);

  const fill = async () => {
    if (!msg.trim()) return;
    try {
      const r = await extract.mutateAsync({ feature: "leave", text: msg.trim() });
      const x = r.fields;
      if (typeof x.type === "string") setType(x.type);
      if (typeof x.from === "string") { setFrom(x.from); setTouched(true); }
      if (typeof x.to === "string") { setTo(x.to); setTouched(true); }
      if (typeof x.note === "string") setNote(x.note);
      setFe({});
      toast(L("Leave request prepared — check the dates and submit", "تم تجهيز طلب الإجازة — راجع التواريخ وأرسل"));
    } catch (e) { toast(errMsg(e), "bad"); }
  };

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { const r = await upload.mutateAsync({ file: f, name: f.name, folder: "leave" }); setAttach({ key: r.key, name: f.name }); toast(L("Attachment uploaded", "تم رفع المرفق")); }
    catch (err) { toast(errMsg(err), "bad"); }
  };

  const submit = async () => {
    const e: Record<string, string> = {};
    if (!from) e.from = L("Choose a start date", "اختر تاريخ البداية");
    if (!to) e.to = L("Choose an end date", "اختر تاريخ النهاية");
    else if (from && to < from) e.to = L("End date must be on or after the start date", "تاريخ النهاية يجب أن يكون بعد البداية أو مساويًا لها");
    setFe(e);
    if (Object.keys(e).length || over) return;
    try {
      await create.mutateAsync({ employeeId: empId, type, from, to, note: note.trim() || undefined, attachmentKey: attach?.key });
      toast(L("Leave request sent to your manager", "تم إرسال طلب الإجازة إلى مديرك"));
      nav("/ess");
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) toast(L("These dates overlap another leave request. ", "هذه التواريخ تتداخل مع طلب إجازة آخر. ") + err.message, "bad");
      else if (err instanceof ApiError && err.fields?.days) toast(L("That's more than your remaining balance.", "هذا أكثر من رصيدك المتبقي."), "bad");
      else { if (err instanceof ApiError && err.fields) setFe(err.fields); toast(errMsg(err), "bad"); }
    }
  };

  return (
    <div className="ess" data-testid="ess-leave">
      <div className="ph"><div><h1>{t("Request leave")}</h1><p data-testid="ess-balance">{L("Annual balance: ", "الرصيد السنوي: ")}{bal.data ? remaining : "—"} {t("days")}</p></div></div>
      <div className="aibar mb">
        <Icon name="sparkle" />
        <div className="grow">
          <b>{L("Just tell us", "اكتب طلبك فقط")}</b>
          <span className="xs muted">{L("Paste a message like “leave 12–26 Nov, family visit”.", "الصق رسالة مثل «إجازة 12–26 نوفمبر لزيارة العائلة».")}</span>
          <label htmlFor={ids.msg} className="sr">{L("Leave message", "رسالة الإجازة")}</label>
          <textarea id={ids.msg} className="inp" rows={2} style={{ marginTop: 6 }} value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="leave-msg" />
        </div>
        <Button className="aibtn" icon="sparkle" loading={extract.isPending} disabled={!msg.trim()} onClick={fill} data-testid="leave-ai">{L("Fill from message", "تعبئة من رسالة")}</Button>
      </div>
      <section className="card card-b stack">
        <Field label={t("Type")} htmlFor={ids.type} hint={policy ? L(policy.en, policy.ar) + " · " + policy.ref : undefined}>
          <Select id={ids.type} value={type} onChange={(e) => setType(e.target.value)} options={(pol.data || [{ type: "Annual", en: "", ar: "", ref: "" }]).map((p) => [p.type, t(p.type)] as [string, string])} data-testid="leave-type" />
        </Field>
        <div className="form2">
          <Field label={t("From")} htmlFor={ids.from} err={fe.from} hint={hDate(from)}><Input id={ids.from} type="date" value={from} bad={!!fe.from} onChange={(e) => { setFrom(e.target.value); setTouched(true); setFe({}); }} data-testid="leave-from" /></Field>
          <Field label={t("To")} htmlFor={ids.to} err={fe.to} hint={hDate(to)}><Input id={ids.to} type="date" value={to} min={from} bad={!!fe.to} onChange={(e) => { setTo(e.target.value); setTouched(true); setFe({}); }} data-testid="leave-to" /></Field>
        </div>
        {!badRange && (
          <Alert tone={over ? "bad" : "info"} tint testId="leave-days"
            title={L(`${cal} calendar days · ${work} working days`, `${cal} يومًا تقويميًا · ${work} أيام عمل`)}
            desc={over ? L("That's more than your remaining balance.", "هذا أكثر من رصيدك المتبقي.") : type === "Annual" ? L("Annual leave is deducted per calendar day. Fridays and Saturdays are not working days.", "تُخصم الإجازة السنوية بالأيام التقويمية. الجمعة والسبت ليست أيام عمل.") : L("Fridays and Saturdays are not working days.", "الجمعة والسبت ليست أيام عمل.")} />
        )}
        {past && <Alert tone="warn" title={L("The start date is in the past — HR will review it as a backdated request.", "تاريخ البداية في الماضي — ستراجعه الموارد البشرية كطلب بأثر رجعي.")} />}
        <Field label={t("Reason")} htmlFor={ids.note}><textarea id={ids.note} className="inp" placeholder={L("Optional", "اختياري")} value={note} onChange={(e) => setNote(e.target.value)} data-testid="leave-note" /></Field>
        <div className="row">
          <label className="btn sm ghost" htmlFor={ids.file} style={{ cursor: "pointer" }}><Icon name="upload" /><span>{attach ? attach.name : L("Attach a medical report (optional)", "إرفاق تقرير طبي (اختياري)")}</span></label>
          <input id={ids.file} type="file" accept="image/*,application/pdf" hidden onChange={onFile} data-testid="leave-file" />
        </div>
      </section>
      <div className="actionbar"><span className="spacer" /><Button variant="pri" icon="check" loading={create.isPending} disabled={over || badRange} onClick={submit} data-testid="leave-submit">{t("Submit")}</Button></div>
      <div className="mt">
        <Card title={L("History", "السجل")} flush testId="leave-history">
          {mine.length === 0 ? <EmptyState title={L("No leave requests", "لا توجد طلبات إجازة")} desc={L("New requests will appear here.", "ستظهر الطلبات الجديدة هنا.")} /> : (
            <div className="tw">
              <table className="tbl cards">
                <thead><tr><th>{t("Type")}</th><th className="hide-md">{t("From")}</th><th className="hide-md">{t("To")}</th><th className="end">{t("Days")}</th><th>{t("Status")}</th></tr></thead>
                <tbody>{mine.map((l) => (
                  <tr key={l.id} data-testid={"leave-" + l.id}>
                    <td className="k1"><b>{t(l.type)}</b>{l.note && <div className="xs muted">{l.note}</div>}</td>
                    <td className="hide-md x" data-l={t("From")}><DualDate date={l.from} /></td>
                    <td className="hide-md x" data-l={t("To")}><DualDate date={l.to} /></td>
                    <td className="end k3" data-l={t("Days")}><span className="num">{l.days}</span></td>
                    <td className="k2"><Chip status={l.status} /></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

export default function EssLeave() {
  const id = useEmployeeId();
  return id == null ? <NoLink /> : <Body empId={id} />;
}
